import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const categories = ['All', 'Websites', 'E-commerce', 'Apps', 'Software'];

const mockProjects = [
  {
    id: '1',
    title: 'TechCorp Website Redesign',
    slug: 'techcorp-redesign',
    description: 'Complete website overhaul with modern design and improved UX',
    category: 'Websites',
    technologies: ['React', 'Node.js', 'MongoDB'],
    image_url: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=800',
    project_url: '#',
    featured: true,
  },
  {
    id: '2',
    title: 'Fashion E-store',
    slug: 'fashion-estore',
    description: 'Modern e-commerce platform with 10,000+ products',
    category: 'E-commerce',
    technologies: ['Shopify', 'React', 'Stripe'],
    image_url: 'https://images.unsplash.com/photo-1557821552-17105176677c?q=80&w=800',
    project_url: '#',
    featured: true,
  },
  {
    id: '3',
    title: 'FinTech Mobile Banking',
    slug: 'fintech-banking',
    description: 'Secure banking app with 50K+ active users',
    category: 'Apps',
    technologies: ['React Native', 'Firebase', 'Node.js'],
    image_url: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?q=80&w=800',
    project_url: '#',
    featured: true,
  },
  {
    id: '4',
    title: 'Hospital Management System',
    slug: 'hospital-system',
    description: 'Complete ERP solution for healthcare',
    category: 'Software',
    technologies: ['Python', 'Django', 'PostgreSQL'],
    image_url: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?q=80&w=800',
    project_url: '#',
    featured: false,
  },
  {
    id: '5',
    title: 'Real Estate Portal',
    slug: 'real-estate-portal',
    description: 'Property listing platform with advanced search',
    category: 'Websites',
    technologies: ['Next.js', 'TypeScript', 'MongoDB'],
    image_url: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?q=80&w=800',
    project_url: '#',
    featured: false,
  },
  {
    id: '6',
    title: 'Food Delivery App',
    slug: 'food-delivery',
    description: 'Multi-restaurant food ordering platform',
    category: 'Apps',
    technologies: ['Flutter', 'Firebase', 'Google Maps'],
    image_url: 'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?q=80&w=800',
    project_url: '#',
    featured: false,
  },
];

export default function PortfolioPage() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [projects, setProjects] = useState(mockProjects);

  const filteredProjects = activeCategory === 'All'
    ? projects
    : projects.filter((p) => p.category === activeCategory);

  return (
    <>
      <Helmet>
        <title>Portfolio - FuturePath Technologies</title>
        <meta name="description" content="Explore our portfolio of successful projects including websites, mobile apps, e-commerce platforms, and custom software solutions." />
      </Helmet>

      <section className="relative min-h-[40vh] flex items-center justify-center pt-32 pb-20">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-secondary/10 to-transparent opacity-50" />
        <div className="container-custom relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-6xl font-bold font-outfit mb-6 text-white" data-testid="portfolio-hero-title">
              Our Portfolio
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
              Showcasing our best work and successful client partnerships
            </p>
          </motion.div>
        </div>
      </section>

      <section className="section-padding" data-testid="portfolio-grid-section">
        <div className="container-custom">
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {categories.map((category) => (
              <Button
                key={category}
                onClick={() => setActiveCategory(category)}
                variant={activeCategory === category ? 'default' : 'outline'}
                className={`rounded-full ${
                  activeCategory === category
                    ? 'bg-primary neon-glow'
                    : 'border-white/20 bg-white/5 hover:bg-white/10'
                }`}
                data-testid={`category-${category.toLowerCase()}`}
              >
                {category}
              </Button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project, index) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group"
                data-testid={`project-${index}`}
              >
                <div className="glass-card rounded-3xl overflow-hidden hover:scale-105 transition-transform duration-500">
                  <div className="aspect-video overflow-hidden relative">
                    <img
                      src={project.image_url}
                      alt={project.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center pb-4">
                      <a
                        href={project.project_url}
                        className="text-white flex items-center gap-2 text-sm"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View Project <ExternalLink size={16} />
                      </a>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="text-xs text-primary font-mono mb-2">{project.category}</div>
                    <h3 className="text-xl font-semibold font-outfit text-white mb-2">
                      {project.title}
                    </h3>
                    <p className="text-muted-foreground text-sm mb-4">
                      {project.description}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {project.technologies.map((tech) => (
                        <span
                          key={tech}
                          className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-mono"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
